import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {Plus, Clock, CheckCircle, XCircle, FileEdit, Ban} from 'lucide-react'
import {useNavigate} from 'react-router-dom'
import {usePageTitle} from '@/hooks/use-page-title'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {RestrictionRuleRequestControllerRequestsEndpoint} from '@/types/endpoint-contracts'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {Button} from '@/components/ui/button'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {PageLayout} from '@/components/shared/PageLayout'
import {Badge} from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {Pagination} from '@/components/ui/pagination'
import {useAuth} from '@/contexts/auth-context'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {permissions} from '@/lib/permissions'
import {routes} from '@/lib/routes'
import {RestrictionRuleRequestStatus, RestrictionRuleRequestType} from '@/types/restriction-rule-request'
import {BadgeVariant} from '@/components/ui/badge'
import {formatDateOnly} from '@/lib/date-utils'
import {badgeWithIconClass} from '@/lib/utils'
import {MutedText} from '@/components/shared/MutedText'
import {IconText} from '@/components/shared/IconText'
import {Icon} from '@/components/shared/Icon'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {TextLink} from '@/components/shared/TextLink'

export function RestrictionRuleRequests() {
    const services = useServices()
    const i18n = useI18n()
    usePageTitle(i18n.restrictionRuleRequests.pageTitle)
    const navigate = useNavigate()
    const {hasPermission} = useAuth()
    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        sortBy, sortDir,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: RestrictionRuleRequestControllerRequestsEndpoint,
        sortable: true,
        defaultSortBy: RestrictionRuleRequestControllerRequestsEndpoint.sort.CREATED_AT,
        defaultSortDir: SortDir.DESC,
    })

    const canCreate = hasPermission(permissions.restrictionRuleRequests.create)

    // Issue 342.07 / closes second half of 244 — empty URL shows ALL requests.
    // The previous implementation defaulted to draft and pending and silently
    // re-applied that selection whenever the user cleared the chips, hiding
    // approved/rejected/cancelled rows from view. The unified bar carries
    // every active filter as a chip — nothing implicit. Sidebar deep links
    // explicitly carry the queued-work statuses to preserve that entry point
    // (see AccessGovernance.tsx menu item, where buildFilterQuery emits the
    // status CSV against the endpoint contract).
    const {data: requestsData} = useQuery({
        queryKey: ['restriction-rule-requests', queryString, page, pageSize, sortBy, sortDir],
        queryFn: () => services.restrictionRuleRequests.list(Object.fromEntries(new URLSearchParams(queryString))),
    })

    const requests = requestsData?.content || []
    const totalItems = requestsData?.totalElements || 0
    const totalPages = requestsData?.totalPages || 0

    const hasActiveFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
    }, [searchAndFilters])

    const statusLabelMap: Record<RestrictionRuleRequestStatus, string> = {
        DRAFT: i18n.restrictionRuleRequests.statusDraft,
        PENDING: i18n.restrictionRuleRequests.statusPending,
        APPROVED: i18n.restrictionRuleRequests.statusApproved,
        REJECTED: i18n.restrictionRuleRequests.statusRejected,
        CANCELLED: i18n.restrictionRuleRequests.statusCancelled,
    }

    const getStatusBadge = (requestStatus: RestrictionRuleRequestStatus) => {
        switch (requestStatus) {
            case RestrictionRuleRequestStatus.DRAFT:
                return {variant: BadgeVariant.SECONDARY, icon: FileEdit}
            case RestrictionRuleRequestStatus.PENDING:
                return {variant: BadgeVariant.WARNING, icon: Clock}
            case RestrictionRuleRequestStatus.APPROVED:
                return {variant: BadgeVariant.SUCCESS, icon: CheckCircle}
            case RestrictionRuleRequestStatus.REJECTED:
                return {variant: BadgeVariant.DESTRUCTIVE, icon: XCircle}
            case RestrictionRuleRequestStatus.CANCELLED:
                return {variant: BadgeVariant.SECONDARY, icon: Ban}
            default:
                return {variant: BadgeVariant.OUTLINE, icon: FileEdit}
        }
    }

    const getRequestTypeLabel = (requestType: RestrictionRuleRequestType) => {
        switch (requestType) {
            case RestrictionRuleRequestType.CREATE:
                return i18n.restrictionRuleRequests.typeCreate
            case RestrictionRuleRequestType.UPDATE:
                return i18n.restrictionRuleRequests.typeUpdate
            case RestrictionRuleRequestType.ACTIVATE:
                return i18n.restrictionRuleRequests.typeActivate
            case RestrictionRuleRequestType.DEACTIVATE:
                return i18n.restrictionRuleRequests.typeDeactivate
            default:
                return requestType
        }
    }

    return (
        <PageLayout
            variant="list"
            icon={CONCEPT_ICON.restrictionRuleRequest}
            title={i18n.restrictionRuleRequests.allRequests}
            count={totalItems}
            description={`${i18n.restrictionRuleRequests.requestCount({count: totalItems})}${hasActiveFilters ? ` ${i18n.common.matchingFilters}` : ''}`}
            data-testid="restriction-rule-requests-list"
            actions={canCreate ? (
                <Button
                    onClick={() => navigate(routes.accessGovernance.restrictionRuleRequests.create())}
                    data-testid="btn-create-request"
                >
                    <IconText icon={Plus}>{i18n.restrictionRuleRequests.newRequest}</IconText>
                </Button>
            ) : undefined}
            filters={
                <UnifiedSearchBar
                    contract={RestrictionRuleRequestControllerRequestsEndpoint}
                    value={searchAndFilters}
                    onChange={setSearchAndFilters}
                    placeholder={i18n.restrictionRuleRequests.searchByName}
                />
            }
            pagination={totalItems > 0 ? (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : undefined}
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead data-testid="col-name">{i18n.common.name}</TableHead>
                        <TableHead data-testid="col-request-type">{i18n.restrictionRuleRequests.requestType}</TableHead>
                        <TableHead data-testid="col-linked-rule">{i18n.restrictionRuleRequests.linkedRule}</TableHead>
                        <TableHead data-testid="col-status">{i18n.common.status}</TableHead>
                        <TableHead data-testid="col-owner-party">{i18n.restrictionRuleRequests.ownerParty}</TableHead>
                        <TableHead data-testid="col-created-at">{i18n.common.createdAt}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {requests.map((request) => {
                        const statusBadge = getStatusBadge(request.status)
                        const StatusIcon = statusBadge.icon
                        return (
                            <ClickableTableRow
                                key={request.id}
                                to={routes.accessGovernance.restrictionRuleRequests.detail(request.id)}
                            >
                                <TableCell className="font-medium">
                                    <TextLink
                                        to={routes.accessGovernance.restrictionRuleRequests.detail(request.id)}
                                        testId={`link-request-detail-${request.id}`}
                                        stopPropagation
                                    >
                                        {request.name}
                                    </TextLink>
                                    {request.description && (
                                        <MutedText size="sm" block className="truncate max-w-xs">
                                            {request.description}
                                        </MutedText>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Badge variant="outline">
                                        {getRequestTypeLabel(request.requestType)}
                                    </Badge>
                                </TableCell>
                                <TableCell>
                                    {request.requestType === RestrictionRuleRequestType.CREATE || !request.ruleId
                                        ? <MutedText>—</MutedText>
                                        : (
                                            <TextLink
                                                to={routes.accessGovernance.restrictionRules.detail(request.ruleId)}
                                                testId="link-request-name"
                                                stopPropagation
                                            >
                                                {request.linkedRuleName ?? request.ruleId}
                                            </TextLink>
                                        )
                                    }
                                </TableCell>
                                <TableCell>
                                    <Badge variant={statusBadge.variant}
                                           className={badgeWithIconClass}>
                                        <Icon icon={StatusIcon} size="xs"/>
                                        {statusLabelMap[request.status] ?? request.status}
                                    </Badge>
                                </TableCell>
                                <TableCell>
                                    {request.owners.map(o => o.ownerPartyName).join(', ') || '—'}
                                </TableCell>
                                <TableCell>
                                    {formatDateOnly(request.createdAt)}
                                </TableCell>
                            </ClickableTableRow>
                        )
                    })}
                    {requests.length === 0 && (
                        <TableEmptyState
                            colSpan={6}
                            hasFilters={hasActiveFilters}
                            matchMessage={i18n.restrictionRuleRequests.noRequestsMatch}
                            emptyMessage={i18n.restrictionRuleRequests.noRequestsFound}
                        />
                    )}
                </TableBody>
            </Table>
        </PageLayout>
    )
}
